import { Observable } from "rxjs/Observable";
import { Subject } from "rxjs/Subject";

import { catchError, first, map, takeUntil } from "rxjs/operators";
import { SharedService } from "../../../../shared/services/shared.service";
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { select } from "@angular-redux/store";
import { PlanningBlock } from "../../../models/planning-block/planning-block.interface";
import { PbScreenService } from "../../../services/pb-screen.service";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { PbActions } from "../../../pb.actions";
import { SharedActions } from "../../../../shared/shared.actions";
import { ToActions } from "../../../../to-screen/to.actions";
import { ToScreenService } from "../../../services/to-screen.service";
import { Chassis } from "../../../../shared/models/chassis.interface";
import { Trailer } from "../../../../shared/models/trailer.interface";
import { IdNameAsStringObject } from "../../../models/quick-view/id-name-as-string-object.interface";
import { LocationSelectorComponent } from "../../../../shared/components/location-selector/location-selector.component";
import { AddressLocation } from "../../../../shared/models/address-location";
import { SimpleModalComponent } from "ngx-simple-modal";
import { ApplicationUser } from "../../../models/application.user.interface";
import { ScreenSource } from "../../../../shared/models/source-screen.enum";


@Component({
    moduleId: module.id,
    selector: "update-pb-modal",
    templateUrl: "./update-pb-modal.component.html"
})
export class UpdatePBModalComponent extends SimpleModalComponent<{ screenSource: ScreenSource }, void> implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("modal") public modal: ElementRef;
    @select("selectedPlanningBlocks") public selectedPlanningBlocks$: Observable<PlanningBlock[]>;
    @select("chassis") public chassis$: Observable<Chassis[]>;
    @select("trailers") public trailers$: Observable<Trailer[]>;
    @select("selectedPbsFromPlanBoard") selectedPbsFromPlanBoard$: Observable<PlanningBlock[]>;
    @select("applicationUser") applicationUser$: Observable<ApplicationUser>;
    @select("containerStatuses") public containerStatuses$: Observable<IdNameAsStringObject[]>;
    @select("loading") public loading$: Observable<boolean>;
    public containerStatuses: IdNameAsStringObject[] = [];
    @ViewChild(LocationSelectorComponent) private locationSelector: LocationSelectorComponent;

    @Input() screenSource: ScreenSource;

    public enableAutocomplete: boolean = false;
    public selectedPlanningBlocks: PlanningBlock[] = [];
    public selectedPlanningBlockIds: number[] = [];
    private selectedLocation: AddressLocation;
    public loadingSpinner: boolean = false;
    public modalHasWarning: boolean = false;
    public modalHasError: boolean = false;
    public planningBlocksHaveDifferentContainerStatuses = true;

    public containerNumber: string = "";
    private containerNumberIsValid: boolean = false;
    public containerNumberErrorDescription: string = "";

    public planningRemark: string = "";
    public haulierRemark: string = "";
    public retourRemark: string = "";
    public selectedStatus: IdNameAsStringObject;

    public trailers: Trailer[] = [];
    public chassis: Chassis[] = [];

    public selectedChassis: Chassis;
    public selectedTrailer: Trailer;

    destroy$ = new Subject();

    applicationUser: ApplicationUser;

    public statusOptions: IdNameAsStringObject[] = [];

    constructor(
        public readonly pbScreenService: PbScreenService,
        public readonly sharedService: SharedService,
        public readonly translate: TranslateService,
        public readonly toastr: ToastrService,
        public readonly pbActions: PbActions,
        public readonly sharedActions: SharedActions,
        public readonly toActions: ToActions,
        public readonly toScreenSerice: ToScreenService) {
            super();
        }

  ngOnInit(): void {

    this.loading$.subscribe((data) => {
      if (!data) {
        this.loadingSpinner = data;
      }
    });

        this.applicationUser$.subscribe((applicationUser: ApplicationUser) => {
            if (applicationUser) {
              this.applicationUser = applicationUser;
              this.enableAutocomplete = applicationUser.generalSetting.enableAutocomplete;
            }
          });
        this.containerStatuses$.subscribe((statuses: IdNameAsStringObject[]) => {
            this.containerStatuses = statuses;
        });


           this.chassis$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
                if (data) {
                    this.chassis = data ;
                } else {
                    this.chassis = [];
                }
           });

            this.trailers$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
                if (data) {
                    this.trailers = data;
                } else {
                    this.trailers = [];
                }
            });

    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit() {
      if (this.screenSource === ScreenSource.PLANNING_BLOCKS_SCREEN) {
        this.selectedPlanningBlocks$.pipe(takeUntil(this.destroy$)).subscribe((pbs: PlanningBlock[]) => {
          if (pbs && pbs.length > 0) {
            this.modalHasWarning = pbs.length > 1;
            this.selectedPlanningBlockIds = [];
            pbs.forEach(pb => this.selectedPlanningBlockIds.push(pb.id));
            this.selectedPlanningBlocks = pbs;
            this.checkContainerNumber();
            this.renderFields(pbs[0]);
            this.setStatuses();
            this.planningBlocksHaveDifferentContainerStatuses = this.determinePlanningBlocksHaveDifferentContainerStatuses();
          }
        });
      } else {
        this.selectedPbsFromPlanBoard$.pipe(takeUntil(this.destroy$)).subscribe((pbs) => {
          if (pbs && pbs.length > 0) {
            this.modalHasWarning = pbs.length > 1;
            this.selectedPlanningBlockIds = [];
            pbs.forEach(pb => this.selectedPlanningBlockIds.push(pb.id));
            this.selectedPlanningBlocks = pbs;
            this.checkContainerNumber();
            this.renderFields(pbs[0]);
            this.setStatuses();
            this.planningBlocksHaveDifferentContainerStatuses = this.determinePlanningBlocksHaveDifferentContainerStatuses();
          }
        });
      }
    }

    setStatuses() {
        if (!this.selectedPlanningBlocks.length) {
            return;
        }
        const status = this.selectedPlanningBlocks[0].containerStatus;
        if (status) {
            this.pbScreenService.getValidContainerStatuses(this.selectedPlanningBlocks[0].orderNumber, status)
                .subscribe((data: string[]) => {
                    data.push(status);
                    this.statusOptions = this.containerStatuses.filter(status => data.includes(status.name));
                });
        } else {
            this.statusOptions = this.containerStatuses;
        }
    }

    private renderFields(pb: PlanningBlock) {
      this.locationSelector.clearSelection();

      this.selectedLocation = this.determineSelectedLocation(pb);
      this.selectedStatus = this.determineSelectedContainerStatus(pb);
      this.planningRemark = this.determinePlanningRemark(pb);
      this.haulierRemark = this.determineHaulierRemark(pb);
      this.retourRemark = this.determineRetourRemark(pb);
      this.containerNumber = this.determineContainerNumber(pb);
      this.containerNumberIsValid = this.hasSinglePb();
    }

    public getSelectedLocation(location: AddressLocation) {
        this.selectedLocation = location;
    }

    public applyUpdate() {
        this.loadingSpinner = true;
        if (this.hasMultiplePbs()) {
            this.handleMultiplePbsUpdate();
        } else {
            const lineNumber = Math.max(...this.selectedPlanningBlocks[0].haulierRemarks.map(d => d.lineNumber));
            const dataLoad = {
                "PbId": this.selectedPlanningBlocks[0].id,
                "Status": this.selectedStatus?.name ?? "",
                "Address": this.selectedLocation ? this.selectedLocation.addressId : "",
                "Location": this.selectedLocation ? this.selectedLocation.locationCode : "",
                "ContainerNumber": this.containerNumber ? this.containerNumber : "",
                "PlanningRemark": this.planningRemark ? this.planningRemark : "",
                "HaulierRemark": this.haulierRemark ? this.haulierRemark : "",
                "RetourRemark": this.retourRemark ? this.retourRemark : "",
                "OrderNumber": this.selectedPlanningBlocks[0].orderNumber ? this.selectedPlanningBlocks[0].orderNumber : " ",
                "LineNumber": (lineNumber && isFinite(lineNumber)) ? lineNumber : 0,
                "TrailerNo" : this.selectedTrailer ? this.selectedTrailer.id : "",
                "ChassisNo" : this.selectedChassis ? this.selectedChassis.id : "",
            };

            this.sharedService.updatePlanningBlock(dataLoad).subscribe(
                (data: PlanningBlock) => {
                    if (data.transportOrderNumber !== "") {
                            this.toScreenSerice.getPlanningBlocksForTO(data.transportOrderNumber).subscribe((data) => {
                                this.toActions.setSelectedTransportOrders([data]);
                                this.sharedActions.updateTransportOrdersDisplay([data]);
                            });
                    }
                    this.pbActions.setSelectedPlanningBlocks([data]);
                    this.sharedActions.updatePlanningBlocksDisplay([data]);
                    this.loadingSpinner = false;
                    this.translateString("TOASTR_MESSAGES.SUCCESS.STATUS_UPDATE_SUCCEEDED").then((result) => {
                        this.toastr.success(result.toString() + data.id + " successfully");
                    });
                    this.closeModal();
                },
                (err) => {
                    this.loadingSpinner = false;
                });
        }
    }

    private handleMultiplePbsUpdate() {
      const multiplePbUpdateParameters = this.selectedPlanningBlocks.map((pb: PlanningBlock) => {
        return {
          "PbId": pb.id,
          "Status": this.planningBlocksHaveDifferentContainerStatuses ? pb.containerStatus ?? "" : this.selectedStatus?.name ?? "",
          "Address": this.selectedLocation ? this.selectedLocation.addressId : "",
          "Location": pb.latestLocationCode ? pb.latestLocationCode : "",
          "ContainerNumber": pb.containerNo ? pb.containerNo : "",
          "PlanningRemark": this.planningRemark !== "" ? this.planningRemark : "",
          "HaulierRemark": this.haulierRemark !== "" ? this.haulierRemark : "",
          "RetourRemark": this.retourRemark !== "" ? this.retourRemark : "",
          "OrderNumber": pb.orderNumber !== "" ? pb.orderNumber : "",
          "TrailerNo": pb.trailerNo ? pb.trailerNo : "",
          "ChassisNo": pb.chassisNo ? pb.chassisNo : "",
        };
      });

      this.sharedService.updatePlanningBlocks(multiplePbUpdateParameters).subscribe(planningBlocks => {
          this.translateString("TOASTR_MESSAGES.SUCCESS.STATUS_UPDATE_SUCCEEDED").then((result) => {
            this.toastr.success(result.toString() + planningBlocks.map(r => r.id).join(", ") + " successfully");
          });

          const tos = [...new Set(planningBlocks.map(pb => pb.transportOrderNumber))];
          tos.forEach((toId) => {
            if (toId !== "") {
              this.toScreenSerice.getPlanningBlocksForTO(toId).subscribe((responseData) => {
                this.toActions.setSelectedTransportOrders([responseData]);
                this.sharedActions.updateTransportOrdersDisplay([responseData]);
              });
            }
          });

          this.pbActions.setSelectedPlanningBlocks(planningBlocks);

          this.sharedActions.updatePlanningBlocksDisplay(planningBlocks);

          this.loadingSpinner = false;
          this.closeModal();
        }, (_ => this.loadingSpinner = false)
      );
    }

    public checkContainerNumber() {
        if (this.containerNumber && this.containerNumber.length > 0) {
            this.sharedService.checkContainerNumber(this.containerNumber).subscribe((res) => {
                if (res.statusCode === 202) {
                    this.containerNumberIsValid = true;
                    this.modalHasError = false;
                }
            }, (err) => {
                this.containerNumberErrorDescription = err.feedback[0].description;
                this.containerNumberIsValid = false;
                this.modalHasError = true;
            });
        } else {
            this.containerNumber = "";
            this.containerNumberIsValid = true;
            this.modalHasError = false;
        }
    }

    public closeModal(): void {
        this.clearFields();
        this.close();
    }

    private clearFields(): void {
        if (this.hasMultiplePbs() || !this.selectedPlanningBlocks.length) {
            this.planningRemark = "";
            this.haulierRemark = "";
            this.retourRemark = "";
        } else {
            if (this.selectedPlanningBlocks[0].planningRemark == "" || this.selectedPlanningBlocks[0].planningRemark == null) {
                this.planningRemark = "";
                this.haulierRemark = "";
                this.retourRemark = "";

            } else {
                this.haulierRemark = this.selectedPlanningBlocks[0].haulierRemark;
                this.retourRemark = this.selectedPlanningBlocks[0].planningBlockRetour;
                this.planningRemark = this.selectedPlanningBlocks[0].planningRemark;

            }
        }

        this.locationSelector.clearSelection();
        this.selectedStatus = {id: "", name: ""};
        this.containerNumber = "";
        this.selectedChassis = null;
        this.selectedTrailer = null;
    }

    public hasMultiplePbs(): boolean {
        return this.selectedPlanningBlocks.length > 1;
    }

  public hasSinglePb(): boolean {
    return !this.hasMultiplePbs();
  }

    determinePlanningBlocksHaveDifferentContainerStatuses(): boolean {
      return this.selectedPlanningBlocks.some(pb => pb.containerStatus !== this.selectedPlanningBlocks[0].containerStatus);
    }

    private translateString(text: string) {
        return this.translate.get(text).pipe(first()).toPromise();
    }
    private haveDifferentAttributeValue(attribute: string): boolean {
      return !this.selectedPlanningBlocks.every(pb => pb[attribute] === this.selectedPlanningBlocks[0][attribute]);
    }

    public autocompleListFormatterForTrailer(data: IdNameAsStringObject): string {
        return `${data.name}`;
    }

    public valueListFormatterForTrailer(data: IdNameAsStringObject): string {
        return `${data.name}`;
    }

    public autocompleListFormatterForChassis(data: IdNameAsStringObject): string {
        return `${data.name}`;
    }

    public valueListFormatterForChassis(data: IdNameAsStringObject): string {
        return `${data.name}`;
    }

  private determineSelectedLocation(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      const { latestLocationCode } = planningBlock;
      return this.locationSelector.setSelectionFromParent(latestLocationCode);
    }

    if (this.haveDifferentAttributeValue("latestLocationCode")) {
      return this.locationSelector.setSelectionFromParent("");
    }

    const { latestLocationCode: latestLocationCode } = this.selectedPlanningBlocks[0];
    return this.locationSelector.setSelectionFromParent(latestLocationCode);
  }

  private determineSelectedContainerStatus(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      const { containerStatus } = planningBlock;
      return this.containerStatuses.find(el => el.name === containerStatus);
    }

    if (this.haveDifferentAttributeValue("containerStatus")) {
      return this.containerStatuses.find(el => el.name === "");
    }

    const { containerStatus: containerStatus } = this.selectedPlanningBlocks[0];
    return this.containerStatuses.find(el => el.name === containerStatus);
  }

  private determinePlanningRemark(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      return planningBlock.planningRemark;
    }

    if (this.haveDifferentAttributeValue("planningRemark")) {
      return "";
    }

    return this.selectedPlanningBlocks[0].planningRemark;
  }

  private determineHaulierRemark(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      return planningBlock.haulierRemark;
    }

    if (this.haveDifferentAttributeValue("haulierRemark")) {
      return "";
    }

    return this.selectedPlanningBlocks[0].haulierRemark;
  }

  private determineRetourRemark(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      return planningBlock.planningBlockRetour;
    }

    if (this.haveDifferentAttributeValue("retourRemark")) {
      return "";
    }

    return this.selectedPlanningBlocks[0].planningBlockRetour;
  }

  private determineContainerNumber(planningBlock: PlanningBlock) {
    if (this.hasSinglePb()) {
      return planningBlock.containerNo;
    }

    if (this.haveDifferentAttributeValue("containerNo")) {
      return "";
    }

    return this.selectedPlanningBlocks[0].containerNo;
  }
}
